/**
 * F12 Rapportage — DTOs + Zod-query-schemas voor operationele en HR-rapporten.
 *
 * Twee dashboards:
 *  - /rapportage      → operationele rapporten (PLANNER/HR/ADMIN)
 *  - /rapportage-hr   → HR-rapporten (HR/ADMIN)
 *
 * Convention:
 *  - Periode-string formaat = "YYYY-MM" (maand-bucket) tenzij anders genoemd.
 *  - Alle bedrag- en uren-velden zijn `number` (geen Decimal in DTO).
 */

import { z } from 'zod'

// ─── Operationele DTO's ───────────────────────────────────────────────────────

/** Uren per periode-bucket (default = maand). Gebruik in lijngrafiek + tabel. */
export interface UrenPerPeriodeDTO {
  /** "YYYY-MM" */
  periode: string
  totaalUren: number
  /** Aantal werkbon-day-entries die meedoen. */
  entries: number
}

/** Omzet per maand (maand × tarief × goedgekeurde uren). */
export interface OmzetPerMaandDTO {
  /** "YYYY-MM" */
  periode: string
  /** Som van uurtarief × goedgekeurde uren + overuren × overuurtarief. */
  bedrag: number
  /** Som van uren (informatief). */
  uren: number
}

/** Top-N klanten naar uren. */
export interface TopKlantenDTO {
  clientId: string
  naam: string
  totaalUren: number
  totaalOmzet: number
}

/** Productiviteit per medewerker = uren / aantal werkbonnen waarop ze stonden. */
export interface ProductiviteitDTO {
  employeeId: string
  naam: string
  totaalUren: number
  aantalWerkbonnen: number
  /** uren / aantalWerkbonnen, 0 als aantal=0. */
  urenPerWerkbon: number
}

/** Reisafstanden-totaal per periode. */
export interface ReisafstandTotalDTO {
  /** "YYYY-MM" */
  periode: string
  totaalKm: number
  totaalMinuten: number
  totaalVergoeding: number
}

// ─── HR DTO's ────────────────────────────────────────────────────────────────

/** Verzuimpercentage per maand of per afdeling+maand (BR #71). */
export interface VerzuimPercentageDTO {
  /** "YYYY-MM" */
  periode: string
  /** Naam van de afdeling, of "Totaal" als geen afdeling-filter actief is. */
  afdeling: string
  /** Werkdagen ziek / totale werkdagen × 100, 2-decimalen. */
  percentage: number
  ziekDagen: number
  werkbareDagen: number
}

/** Verlofgebruik per emmer per medewerker (toegekend vs gebruikt). */
export interface VerlofgebruikDTO {
  employeeId: string
  naam: string
  /** WETTELIJK / BOVENWETTELIJK / ATV / TVT_WERK / TVT_REIS */
  emmer: string
  toegekend: number
  gebruikt: number
  saldo: number
}

/** TVT-saldo overzicht (opbouw vs opname per medewerker). */
export interface TVTSaldoDTO {
  employeeId: string
  naam: string
  opbouwUren: number
  opnameUren: number
  saldo: number
}

/** Onboarding-doorlooptijd: dagen tussen CONCEPT-creatie en AFGEROND. */
export interface OnboardingDoorlooptijdDTO {
  /** "YYYY-MM" */
  periode: string
  aantalAfgerond: number
  /** Gemiddelde dagen, 1-decimaal. 0 als aantalAfgerond=0. */
  gemiddeldeDagen: number
}

/** Certificaat-verloop: aantal certificaten dat verloopt binnen X dagen. */
export interface CertificaatVerloopDTO {
  certificateTypeId: string
  certificaatNaam: string
  /** Aantal expirerend ≤ 60 dagen. */
  binnen60: number
  /** Aantal expirerend ≤ 90 dagen (cumulatief — incl 60). */
  binnen90: number
  /** Aantal expirerend ≤ 180 dagen (cumulatief). */
  binnen180: number
  /** Aantal reeds verlopen op peildatum. */
  verlopen: number
}

/** Headcount + FTE-trend per maand. */
export interface HeadcountTrendDTO {
  /** "YYYY-MM" */
  periode: string
  headcount: number
  fte: number
}

// ─── Zod-query-schemas ───────────────────────────────────────────────────────

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/u, 'Verwacht YYYY-MM-DD')

export const urenPerPeriodeQuerySchema = z.object({
  vanaf: isoDate,
  totEnMet: isoDate,
  klant: z.string().optional(),
})

export const omzetPerMaandQuerySchema = z.object({
  jaar: z.coerce.number().int().min(2000).max(2100),
})

export const topKlantenQuerySchema = z.object({
  vanaf: isoDate,
  totEnMet: isoDate,
  limit: z.coerce.number().int().min(1).max(100).optional().default(10),
})

export const productiviteitQuerySchema = z.object({
  vanaf: isoDate,
  totEnMet: isoDate,
  medewerker: z.string().optional(),
})

export const reisafstandTotalsQuerySchema = z.object({
  vanaf: isoDate,
  totEnMet: isoDate,
})

export const verzuimPercentageQuerySchema = z.object({
  vanaf: isoDate,
  totEnMet: isoDate,
  afdeling: z.string().optional(),
})

export const verlofgebruikQuerySchema = z.object({
  jaar: z.coerce.number().int().min(2000).max(2100),
})

export const tvtSaldiQuerySchema = z.object({
  jaar: z.coerce.number().int().min(2000).max(2100),
})

export const onboardingQuerySchema = z.object({
  vanaf: isoDate,
  totEnMet: isoDate,
})

export const certificaatVerloopQuerySchema = z.object({
  /** Niet gebruikt als query-param-input — server splitst zelf naar 60/90/180. */
  dagen: z.coerce.number().int().min(1).max(365).optional().default(60),
})

export const headcountTrendQuerySchema = z.object({
  jaar: z.coerce.number().int().min(2000).max(2100),
})

export type UrenPerPeriodeQuery = z.infer<typeof urenPerPeriodeQuerySchema>
export type OmzetPerMaandQuery = z.infer<typeof omzetPerMaandQuerySchema>
export type TopKlantenQuery = z.infer<typeof topKlantenQuerySchema>
export type ProductiviteitQuery = z.infer<typeof productiviteitQuerySchema>
export type ReisafstandTotalsQuery = z.infer<typeof reisafstandTotalsQuerySchema>
export type VerzuimPercentageQuery = z.infer<typeof verzuimPercentageQuerySchema>
export type VerlofgebruikQuery = z.infer<typeof verlofgebruikQuerySchema>
export type TVTSaldiQuery = z.infer<typeof tvtSaldiQuerySchema>
export type OnboardingQuery = z.infer<typeof onboardingQuerySchema>
export type CertificaatVerloopQuery = z.infer<typeof certificaatVerloopQuerySchema>
export type HeadcountTrendQuery = z.infer<typeof headcountTrendQuerySchema>

// ─── Fase 7.4 — Mutaties-histogram (distributie per type) ───────────────────
//
// HR-rapport-tegel: per MutatieType (ADRES, BANK, NOODCONTACT, TELEFOON, EMAIL)
// het totaal aantal verzoeken in een periode, optioneel uitgesplitst per
// status (INGEDIEND/GOEDGEKEURD/AFGEKEURD/INGETROKKEN). De distributie is
// nuttig voor capaciteits-planning (welke wijzigingen domineren?) en voor
// het vinden van data-kwaliteits-issues (veel adres-mutaties → bron-systeem
// loopt niet synchroon mee).

export interface MutatieHistogramBucketDTO {
  /** Eén van de 5 mutatie-types. */
  type: string
  totaal: number
  ingediend: number
  goedgekeurd: number
  afgekeurd: number
  ingetrokken: number
}

export interface MutatiesHistogramDTO {
  buckets: MutatieHistogramBucketDTO[]
  /** Totaal alle types samen. */
  totaal: number
  /** Begin en einde van de periode (ISO date strings). */
  vanaf: string
  totEnMet: string
}

export const mutatiesHistogramQuerySchema = z.object({
  vanaf: isoDate,
  totEnMet: isoDate,
})
export type MutatiesHistogramQuery = z.infer<typeof mutatiesHistogramQuerySchema>

// ─── Batch-8 rapportage — afgeweken uren t.o.v. werkbon (Deel 1) ─────────────
//
// Per gesloten/ingevulde `WerkbonDayEntry`: geplande uren (uit de dag-/werkbon-
// tijden minus pauze) versus geschreven uren (`hours` + `overuren`). Zowel
// positieve (meer geschreven dan gepland) als negatieve afwijkingen tellen mee.
// Eén geaggregeerde response: detail-rijen + aggregaties per medewerker en per
// klant. Alle uren-velden zijn `number` (Decimal aan de DB-kant; conversie in de
// route met `asNumber`).

/** Eén afgeweken werkbon-dag (gepland vs. geschreven). */
export interface AfwijkingUrenRowDTO {
  dayEntryId: string
  werkbonId: string
  werkbonNummer: string
  /** "YYYY-MM-DD" (NL-kalenderdag). */
  datum: string
  employeeId: string
  medewerker: string
  clientId: string | null
  klant: string | null
  geplandeUren: number
  geschrevenUren: number
  /** geschreven − gepland, +/−, 2 decimalen. */
  afwijkingUren: number
  /** Bestaande afwijking-toelichting op de day-entry (indien ingevuld). */
  toelichting: string | null
}

/** Aggregatie per medewerker over de periode. */
export interface AfwijkingUrenMedewerkerDTO {
  employeeId: string
  medewerker: string
  /** Som van de afwijkingen (+/−). */
  totaalAfwijking: number
  /** Som van |afwijking| (grootte, ongeacht richting). */
  totaalAbsoluteAfwijking: number
  aantalDagen: number
  /** totaalAfwijking / aantalDagen, 2 decimalen. */
  gemiddeldeAfwijking: number
}

/** Aggregatie per klant over de periode. */
export interface AfwijkingUrenKlantDTO {
  clientId: string
  klant: string
  totaalAfwijking: number
  totaalAbsoluteAfwijking: number
  aantalDagen: number
  gemiddeldeAfwijking: number
}

export interface AfwijkingenUrenResponseDTO {
  rows: AfwijkingUrenRowDTO[]
  perMedewerker: AfwijkingUrenMedewerkerDTO[]
  perKlant: AfwijkingUrenKlantDTO[]
  /** Som van alle afwijkingen (+/−) over de periode. */
  totaalAfwijking: number
  /** Aantal dagen met een afwijking ≥ de drempel. */
  totaalDagen: number
  /** Grootste absolute afwijker (medewerker) — null als er geen rijen zijn. */
  grootsteAfwijker: { employeeId: string; medewerker: string; afwijking: number } | null
}

export const afwijkingenUrenQuerySchema = z.object({
  vanaf: isoDate,
  totEnMet: isoDate,
  medewerker: z.string().optional(),
  klant: z.string().optional(),
  /** BV/entiteit-filter (Werkbon.entityId). */
  bv: z.string().optional(),
  /** Minimale absolute afwijking in uren (default 0,25 u = 15 min). */
  minAfwijking: z.coerce.number().min(0).max(24).optional().default(0.25),
})
export type AfwijkingenUrenQuery = z.infer<typeof afwijkingenUrenQuerySchema>

// ─── Batch-8 rapportage — afgeweken reistijden per medewerker (Deel 2) ───────
//
// Bron: `ReisUrenBerekening` waar `werkelijkeReistijdUren` gevuld is. Afwijking =
// werkelijk − berekend (uren én km), per medewerker per klant per richting.
// `berekende*` bevat de structurele correctie al, dus een traject met een
// passende standaard levert ~0 afwijking (zie reisafstanden.md). Reistijd wordt
// in de UI in minuten getoond; de DTO levert minuten (afgeleid van de uren-bron).

export const REISTIJD_RICHTING = ['HEEN', 'TERUG', 'BEIDE'] as const
export type ReistijdRichtingFilter = (typeof REISTIJD_RICHTING)[number]

/** Aggregatie per medewerker→klant-combinatie (de standaard-eenheid van de override). */
export interface AfwijkingReistijdRowDTO {
  employeeId: string
  medewerker: string
  clientId: string | null
  klant: string
  /** 'HEEN' (woon→klant) of 'TERUG' (klant→woon). */
  richting: 'HEEN' | 'TERUG'
  /** Berekende (standaard) reistijd in minuten, gemiddeld over de ritten. */
  verwachtMinuten: number
  /** Gemiddelde werkelijke reistijd in minuten. */
  gemiddeldWerkelijkMinuten: number
  /** Gemiddelde afwijking (werkelijk − berekend) in minuten, +/−. */
  gemiddeldeAfwijkingMinuten: number
  /** Mediaan-afwijking in minuten, +/−. */
  medianAfwijkingMinuten: number
  /** Gemiddelde km-afwijking (werkelijk − berekend), +/−. */
  gemiddeldeAfwijkingKm: number
  aantalRitten: number
  /** Aandeel ritten dat dezelfde kant op wijkt (0..1) — consistentie-indicator. */
  consistentie: number
  /** Er loopt een OPEN aanpas-signaal voor deze medewerker→klant→richting. */
  heeftSignaal: boolean
}

/** Aggregatie per medewerker (totaal over alle klanten). */
export interface AfwijkingReistijdMedewerkerDTO {
  employeeId: string
  medewerker: string
  aantalRitten: number
  totaleAfwijkingMinuten: number
  gemiddeldeAfwijkingMinuten: number
}

export interface AfwijkingenReistijdResponseDTO {
  rows: AfwijkingReistijdRowDTO[]
  perMedewerker: AfwijkingReistijdMedewerkerDTO[]
  totaalRitten: number
}

export const afwijkingenReistijdQuerySchema = z.object({
  vanaf: isoDate,
  totEnMet: isoDate,
  medewerker: z.string().optional(),
  klant: z.string().optional(),
  bv: z.string().optional(),
  richting: z.enum(REISTIJD_RICHTING).optional().default('BEIDE'),
  /** Minimale absolute mediaan-afwijking in minuten om in het overzicht te tonen. */
  minAfwijkingMinuten: z.coerce.number().min(0).max(600).optional().default(5),
})
export type AfwijkingenReistijdQuery = z.infer<typeof afwijkingenReistijdQuerySchema>
